/**
 * networkAvailabilityService.ts
 * Frontend service for network availability and location capabilities
 */

import { auth } from '@/config/firebase';
import type { NetworkAvailability, AggregatedCapabilities } from '@simcheck/shared-types';
import { FUNCTIONS_URL, CENTRAL_API_URL } from '@/config/env';

// API base URL — Firebase Functions base
const API_URL = FUNCTIONS_URL;

// CENTRAL_API_URL (central-server, raw Cloud Run) holds the live edge-device + modem registry.

/**
 * Get auth headers with Firebase ID token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
}

export const networkAvailabilityService = {
  /**
   * Get network availability for a specific network
   */
  async getNetworkAvailability(networkId: string): Promise<NetworkAvailability | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/api/networks/${networkId}/availability`,
        { headers }
      );

      if (!response.ok) {
        if (response.status === 404) return null;
        const error = await response.json().catch(() => ({ message: 'Failed to fetch network availability' }));
        throw new Error(error.message || 'Failed to fetch network availability');
      }

      const data = await response.json();
      return data.availability || data;
    } catch (error) {
      console.error('Error fetching network availability:', error);
      throw error;
    }
  },

  /**
   * Get all networks available at a specific location
   */
  async getNetworksAtLocation(locationId: string): Promise<NetworkAvailability[]> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/api/locations/${locationId}/networks`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch networks at location' }));
        throw new Error(error.message || 'Failed to fetch networks at location');
      }

      const data = await response.json();
      return data.networks || data;
    } catch (error) {
      console.error('Error fetching networks at location:', error);
      throw error;
    }
  },

  /**
   * Get network availability summary (all networks)
   */
  async getAvailabilitySummary(): Promise<{
    totalNetworks: number;
    totalModems: number;
    totalLocations: number;
    staleCount: number;
    networks: NetworkAvailability[];
  }> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/api/networks/availability/summary`,
        { headers }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch availability summary' }));
        throw new Error(error.message || 'Failed to fetch availability summary');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching availability summary:', error);
      throw error;
    }
  },

  /**
   * Get aggregated capabilities for a location
   * Fetches from Central Server (simcheck-server) where edge devices/modems are stored
   */
  async getLocationCapabilities(locationId: string): Promise<AggregatedCapabilities | null> {
    try {
      const headers = await getAuthHeaders();
      const url = `${CENTRAL_API_URL}/locations/${locationId}/capabilities`;
      console.log(`[NetworkAvailability] Fetching capabilities for location ${locationId} from ${url}`);

      // Use Central Server API - edge devices with modem capabilities are in simcheck-server
      const response = await fetch(url, { headers });

      console.log(`[NetworkAvailability] Response status: ${response.status}`);

      if (!response.ok) {
        if (response.status === 404) return null;
        const errorBody = await response.text();
        console.error(`[NetworkAvailability] Error response: ${errorBody}`);
        throw new Error(`Failed to fetch location capabilities: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[NetworkAvailability] Capabilities for ${locationId}:`, data);
      return data.capabilities || data;
    } catch (error) {
      console.error('Error fetching location capabilities:', error);
      throw error;
    }
  },

  /**
   * Trigger recalculation of location capabilities
   */
  async recalculateLocationCapabilities(locationId: string): Promise<AggregatedCapabilities | null> {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/api/locations/${locationId}/capabilities/recalculate`,
        {
          method: 'POST',
          headers
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to recalculate capabilities' }));
        throw new Error(error.message || 'Failed to recalculate capabilities');
      }

      const data = await response.json();
      return data.capabilities || data;
    } catch (error) {
      console.error('Error recalculating location capabilities:', error);
      throw error;
    }
  },

  /**
   * Get aggregated availability for all networks
   * Fetches from Central Server and aggregates by MCC/MNC
   */
  async getAllNetworksAvailability(): Promise<Map<string, { modemCount: number; locationCount: number; isStale: boolean }>> {
    try {
      const headers = await getAuthHeaders();
      const url = `${CENTRAL_API_URL}/networks/availability/summary`;
      console.log(`[NetworkAvailability] Fetching all networks availability from ${url}`);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        // API might not exist yet, return empty map
        if (response.status === 404) {
          console.log('[NetworkAvailability] Summary endpoint not found, returning empty map');
          return new Map();
        }
        throw new Error(`Failed to fetch networks availability: ${response.status}`);
      }

      const data = await response.json();
      const availabilityMap = new Map<string, { modemCount: number; locationCount: number; isStale: boolean }>();

      // Process the response data
      if (data.networks && Array.isArray(data.networks)) {
        for (const network of data.networks) {
          const key = `${network.mcc}-${network.mnc}`;
          availabilityMap.set(key, {
            modemCount: network.modemCount || 0,
            locationCount: network.locationCount || 0,
            isStale: network.isStale || false
          });
        }
      }

      return availabilityMap;
    } catch (error) {
      console.error('Error fetching networks availability:', error);
      return new Map();
    }
  }
};
